/**
 * Billing Service
 *
 * Handles all billing-related API calls for the frontend
 */

import api from '../config/axiosConfig';
import { BILLING_URLS, BILLING_STATUS, BillingStatusType } from '../config/Constants';

export interface BillingStatusDisplay {
  text: string;
  color: string;
  icon: string;
}

export interface BillingInfo {
  status: BillingStatusType;
  billingEnabled?: boolean;
  subscriptionStatus?: string;
  payMethod?: 'autoRenew' | 'invoice' | string;
  paymentMethodDisplay?: string;
  isAutoRenewal?: boolean;
  inheritedFromParent?: boolean;
  billingClientId?: string;
  childClientId?: string;
  // Game-based trial fields
  freeGamesUsed: number;
  freeGamesLimit: number;
  freeGamesRemaining: number;
  trialStartedAt?: string;
  trialEndedAt?: string;
  // Renewal info
  currentPeriodEnd?: string;
  /** Set during renewal lifecycle (INVOICE_SENT / PAST_DUE); API may include when relevant */
  nextPeriodEnd?: string | null;
  daysUntilRenewal?: number | null;
  renewalInvoiceSentAt?: string;
  // Grace period info
  graceDeadline?: string;
  daysUntilSuspension?: number | null;
  gracePeriodDays?: number;
  // Cancellation info
  cancelAtPeriodEnd?: boolean;
  // Other billing info
  lastPaymentAt?: string;
  hasStripeSetup: boolean;
  statusDisplay?: BillingStatusDisplay;
  isActive?: boolean;
  isSuspended?: boolean;
  isPastDue?: boolean;
  isRenewalPending?: boolean;
}

export interface BillingStatusResponse {
  success: boolean;
  billing?: BillingInfo;
  message?: string;
}

export const isBillingEnabled = (billing?: BillingInfo | null): boolean => {
  return billing?.billingEnabled !== false;
};

export interface SubscriptionResponse {
  success: boolean;
  subscription?: {
    status: string;
    currentPeriodEnd: string;
  } | null;
  message?: string;
}

const normalizeNonNegativeInt = (value: unknown, fallback = 0): number => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.floor(n));
};

const normalizeBillingCounters = (billing: BillingInfo): BillingInfo => {
  const freeGamesLimit = normalizeNonNegativeInt(billing.freeGamesLimit, 3);
  const freeGamesUsed = normalizeNonNegativeInt(billing.freeGamesUsed, 0);
  return {
    ...billing,
    freeGamesLimit,
    freeGamesUsed,
    freeGamesRemaining: Math.max(0, freeGamesLimit - freeGamesUsed),
  };
};

/**
 * Get the billing status for the current user's team
 */
export const getBillingStatus = async (): Promise<BillingStatusResponse> => {
  try {
    const response = await api.get(BILLING_URLS.STATUS);
    if (response.data?.success && response.data?.billing) {
      return {
        ...response.data,
        billing: normalizeBillingCounters(response.data.billing),
      };
    }
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch billing status:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch billing status',
    };
  }
};

/**
 * Get subscription details
 */
export const getSubscription = async (): Promise<SubscriptionResponse> => {
  try {
    const response = await api.get(BILLING_URLS.SUBSCRIPTION);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch subscription:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch subscription',
    };
  }
};

/**
 * Get display info for a billing status
 */
export const getBillingStatusDisplay = (status: BillingStatusType): BillingStatusDisplay => {
  const displays: Record<BillingStatusType, BillingStatusDisplay> = {
    [BILLING_STATUS.TRIAL]: { text: 'Free Trial', color: 'yellow', icon: '⏰' },
    [BILLING_STATUS.INVOICE_DRAFT]: { text: 'Invoice Preparing', color: 'purple', icon: '📝' },
    [BILLING_STATUS.PENDING]: { text: 'Invoice Sent', color: 'blue', icon: '📧' },
    [BILLING_STATUS.ACTIVE]: { text: 'Active', color: 'green', icon: '✅' },
    [BILLING_STATUS.INVOICE_SENT]: { text: 'Renewal Invoice Sent', color: 'blue', icon: '📧' },
    [BILLING_STATUS.PAST_DUE]: { text: 'Past Due - Grace Period', color: 'orange', icon: '⚠️' },
    [BILLING_STATUS.SUSPENDED]: { text: 'Suspended - Payment Required', color: 'red', icon: '🚫' },
    [BILLING_STATUS.CANCELED]: { text: 'Canceled', color: 'gray', icon: '❌' },
  };

  return displays[status] || { text: 'Unknown', color: 'gray', icon: '❓' };
};

/**
 * Check if the billing status allows normal app usage
 * Trial users can use app until 3 free games are exhausted
 * Renewal pending and past due (within grace) can still use app
 */
export const isBillingActive = (status: BillingStatusType): boolean => {
  const activeStatuses: BillingStatusType[] = [
    BILLING_STATUS.TRIAL,
    BILLING_STATUS.INVOICE_DRAFT, // Still allowed while invoice is being prepared
    BILLING_STATUS.ACTIVE,
    BILLING_STATUS.PENDING, // Can use during 3 free trial games
    BILLING_STATUS.INVOICE_SENT, // Renewal date passed, within net-30 (full access)
    BILLING_STATUS.PAST_DUE, // Still allowed during 14-day grace period
  ];

  return activeStatuses.includes(status);
};

/**
 * Check if account is suspended
 */
export const isSuspended = (status: BillingStatusType): boolean => {
  return status === BILLING_STATUS.SUSPENDED;
};

/**
 * Format a date for display
 */
export const formatBillingDate = (dateString?: string): string => {
  if (!dateString) return 'N/A';

  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Calculate days remaining until a date
 */
export const getDaysRemaining = (dateString?: string): number | null => {
  if (!dateString) return null;

  try {
    const targetDate = new Date(dateString);
    const now = new Date();
    const diffTime = targetDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  } catch {
    return null;
  }
};

const billingService = {
  getBillingStatus,
  getSubscription,
  isBillingEnabled,
  getBillingStatusDisplay,
  isBillingActive,
  isSuspended,
  formatBillingDate,
  getDaysRemaining,
};

export default billingService;
